import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { sortBy } from 'lodash'
import { Button, DialogActions, DialogContent } from '@material-ui/core'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { Dialog } from '../dialog/Dialog'
import { validators } from '../../validation/validators'
import { ControlledChecklist } from '../form/ControlledChecklist'
import { useUsersQuery } from '../../generated/graphql'

export type FormValues = {
  name: string
  userIds: string[]
}

type Props = {
  isOpen: boolean
  defaultValues?: Partial<FormValues>
  onSubmit: (values: FormValues) => void
  onClose: () => void
}

export function UserGroupDialog({
  isOpen,
  defaultValues,
  onSubmit,
  onClose,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, handleSubmit, errors, reset } = useForm<FormValues>({
    defaultValues,
  })
  const { data: usersData } = useUsersQuery()

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const isEditing = Boolean(defaultValues?.name)
  const users = usersData?.users ?? []

  const sortedUsers = sortBy(users, (user) => {
    const prefix = defaultValues?.userIds?.includes(user.id) ? '1' : '2'

    return `${prefix}-${user.displayName}`
  })

  return (
    <Dialog
      title={defaultValues?.name ?? t('userGroups.create')}
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="name"
              rules={validators.required}
              error={errors.name}
              label={t('userGroups.dialog.name')}
            />
          </FormGroup>

          <FormGroup>
            <ControlledChecklist
              control={control}
              name="userIds"
              label={t('userGroups.dialog.users')}
              options={sortedUsers}
              keyGetter={(user) => user.id}
              labelGetter={(user) => user.displayName}
              isFilterable
            />
          </FormGroup>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {isEditing ? t('common.saveChanges') : t('userGroups.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
